import { gql, QueryHookOptions, QueryResult, useQuery } from '@apollo/client'
import type {
  GetPublicProfileQuery,
  GetPublicProfileQueryVariables,
} from 'apollo-gql'
import featuredVideosFragment from 'graphql/fragments/featuredVideosFragment'
import { currentMarketIdVar } from 'graphql/reactive'

import fullPublicProfileProduct from '../graphql/fragments/fullPublicProfileProduct'

export const PROFILE_FRAGMENT = gql`
  fragment fullPublicProfile on PublicProfile {
    id
    name
    slug
    title
    description
    typicallyRespondsInDays
    seoContent
    averageReviewRating
    reviewCount

    defaultMarketId
    acceptingOrders
    defaultProfileProduct {
      ...fullPublicProfileProduct
    }
    ...featuredVideosFragment

    profileMarkets {
      showDirect
      market {
        id
      }
    }

    profileProducts {
      edges {
        node {
          ...fullPublicProfileProduct
        }
      }
    }

    profilePicture {
      id
      avatarUrl: url(
        format: { width: 100, height: 100, fit: crop, mask: ellipse }
      )
      bigUrl: url(format: { width: 400, height: 740, fit: crop })
      cardUrl: url(format: { width: 265, height: 458, fit: crop })
      meanBackgroundColorHex
    }
    profileVideo {
      id
      url(preset: VIDEO_DEFAULT)
      mp4Url
    }
    featureFlags {
      donation
    }
    languagesSpoken {
      id
      label
    }
    mainTag {
      id
      profileCollection {
        slug
        publicCmsItem {
          title
          color
        }
      }
      visibleInNavigation
      parentTag {
        id
        profileCollection {
          slug
          publicCmsItem {
            title
            color
          }
        }
        visibleInNavigation
        parentTag {
          id
          profileCollection {
            slug
            publicCmsItem {
              title
              color
            }
          }
          visibleInNavigation
          parentTag {
            id
            profileCollection {
              slug
              publicCmsItem {
                title
                color
              }
            }
            visibleInNavigation
          }
        }
      }
    }

    hasB2cProduct
    hasB2bProduct
  }
  ${fullPublicProfileProduct}
  ${featuredVideosFragment}
`

export const GET_PUBLIC_PROFILE = gql`
  query GetPublicProfile($marketId: ID!, $slug: String!) {
    market(id: $marketId) {
      id
      profile(slug: $slug) {
        ...fullPublicProfile
      }
    }
  }
  ${PROFILE_FRAGMENT}
`

type UsePublicProfileReturn = {
  profile: GetPublicProfileQuery['market']['profile']
} & Pick<
  QueryResult<GetPublicProfileQuery, GetPublicProfileQueryVariables>,
  'error' | 'loading' | 'networkStatus'
>

export default function usePublicProfile(
  slug: GetPublicProfileQueryVariables['slug'],
  useQueryParams: QueryHookOptions<
    GetPublicProfileQuery,
    GetPublicProfileQueryVariables
  > = {},
): UsePublicProfileReturn {
  const marketId = currentMarketIdVar()

  const { loading, data, networkStatus, error } = useQuery<
    GetPublicProfileQuery,
    GetPublicProfileQueryVariables
  >(GET_PUBLIC_PROFILE, {
    variables: { slug, marketId },
    ...useQueryParams,
    returnPartialData: true,
  })

  const profile = data?.market?.profile

  return { profile, loading, error, networkStatus }
}
